import gql from 'graphql-tag';

import GraphQLClient from '../../adapter/GraphQLClient';
import Account, { AccountKind, AccountStatus } from '../../entity/Account';

class AccountWebApi {
  private graphqlClient: GraphQLClient;

  constructor({ idGraphqlClient }: { idGraphqlClient: GraphQLClient }) {
    this.graphqlClient = idGraphqlClient;
  }

  public getAccount = async (id: string): Promise<Account> => {
    const result = await this.graphqlClient.query(
      gql`
        query account($id: ID!) {
          account(id: $id) {
            id
            companyName
            representativeName
            companyUrl
            departmentName
            tel
            kind
            status
            questionnaireAnswer
          }
        }
      `,
      { id },
    );
    return Account.fromJSON(result.account);
  };

  public updateAccount = async ({
    id,
    companyName,
    representativeName,
    companyUrl,
    departmentName,
    tel,
    kind,
  }: {
    id: string;
    companyName: string;
    representativeName: string;
    companyUrl: string;
    departmentName: string;
    tel: string;
    kind: AccountKind;
  }): Promise<void> => {
    await this.graphqlClient.query(
      gql`
        mutation UpdateAccount(
          $id: ID!
          $companyName: String
          $companyUrl: String
          $departmentName: String
          $kind: AccountKindEnum!
          $representativeName: String
          $tel: String!
        ) {
          updateAccount(
            input: {
              id: $id
              companyName: $companyName
              companyUrl: $companyUrl
              departmentName: $departmentName
              kind: $kind
              representativeName: $representativeName
              tel: $tel
            }
          ) {
            account {
              id
            }
          }
        }
      `,
      { id, companyName, representativeName, companyUrl, departmentName, tel, kind },
    );
  };

  public createAccount = async ({
    companyName,
    representativeName,
    companyUrl,
    departmentName,
    tel,
    country,
    kind,
  }: {
    companyName: string;
    representativeName: string;
    companyUrl: string;
    departmentName: string;
    tel: string;
    country: string;
    kind: AccountKind;
  }): Promise<AccountStatus> => {
    const result = await this.graphqlClient.query(
      gql`
        mutation CreateAccount(
          $companyName: String
          $companyUrl: String
          $departmentName: String
          $kind: AccountKindEnum!
          $representativeName: String
          $tel: String!
          $country: String
        ) {
          createAccount(
            input: {
              companyName: $companyName
              companyUrl: $companyUrl
              departmentName: $departmentName
              kind: $kind
              representativeName: $representativeName
              tel: $tel
              country: $country
            }
          ) {
            account {
              id
              status
            }
          }
        }
      `,
      { companyName, representativeName, companyUrl, departmentName, tel, country, kind },
    );
    return result.createAccount.account.status as AccountStatus;
  };

  public createAccountWithQuestionnaire = async ({
    companyName,
    representativeName,
    companyUrl,
    departmentName,
    tel,
    country,
    kind,
    questionnaireAnswer,
    isGlobal,
  }: {
    companyName: string;
    representativeName: string;
    companyUrl: string;
    departmentName: string;
    tel: string;
    country: string;
    kind: AccountKind;
    questionnaireAnswer?: string;
    isGlobal?: boolean;
  }): Promise<Account> => {
    const result = await this.graphqlClient.query(
      gql`
        mutation CreateAccount(
          $companyName: String
          $companyUrl: String
          $departmentName: String
          $kind: AccountKindEnum!
          $representativeName: String
          $tel: String!
          $country: String
          $questionnaireAnswer: String
          $isGlobal: Boolean
        ) {
          createAccount(
            input: {
              companyName: $companyName
              companyUrl: $companyUrl
              departmentName: $departmentName
              kind: $kind
              representativeName: $representativeName
              tel: $tel
              country: $country
              questionnaireAnswer: $questionnaireAnswer
              isGlobal: $isGlobal
            }
          ) {
            account {
              id
              companyName
              companyUrl
              representativeName
              departmentName
              tel
              kind
              status
              questionnaireAnswer
            }
          }
        }
      `,
      {
        companyName,
        representativeName,
        companyUrl,
        departmentName,
        tel,
        country,
        kind,
        questionnaireAnswer,
        isGlobal,
      },
    );
    return result.createAccount.account as Account;
  };
}

export default AccountWebApi;
